// Central place for a developer's billing — hosting-subscription invoices for
// hosted agents, and premium chat-unlock invoices for any agent (including
// webhook/external ones, which have no hosting subscription but can still
// earn from premium chats). Reachable from the account dropdown, so a
// developer isn't stuck hunting through an agent's modal to find this.
import { Navigate, Link } from 'react-router-dom'
import { useAppSelector } from '../hooks/useAppDispatch'
import { useGetDeveloperMeQuery } from '../store/api/developerApi'
import { AgentBillingPanel } from '../features/billing/AgentBillingPanel'
import { SettingsBilling } from '../features/settings/SettingsBilling'

export default function BillingPage() {
  const { isAuthenticated } = useAppSelector(s => s.auth)
  const { data, isLoading } = useGetDeveloperMeQuery(undefined, { skip: !isAuthenticated })

  if (!isAuthenticated) return <Navigate to="/" replace />

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div style={{ width: 20, height: 20, border: '2px solid var(--border)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    )
  }

  const agents = data?.application.agents ?? []

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '2rem 1.25rem' }}>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 0.3rem' }}>🧾 Billing</h1>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 1.5rem' }}>
        Invoices for what you have paid for, plus hosting subscription status and premium unlock invoices for your agents.
      </p>

      <div style={{ marginBottom: '1.5rem' }}>
        <SettingsBilling includeHosting={false} />
      </div>

      {agents.length === 0 && (
        <div style={{ padding: '1.5rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          No agents yet. Register one from the{' '}
          <Link to="/developer" style={{ color: 'var(--accent-primary)' }}>Creator Portal</Link> to see its billing here.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {agents.map(agent => (
          <div key={agent.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.9rem' }}>
              <span style={{ fontSize: '1.1rem' }}>{agent.icon || '🤖'}</span>
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{agent.name}</span>
            </div>
            <AgentBillingPanel agentId={agent.id} agentType={agent.agentType} />
          </div>
        ))}
      </div>
    </div>
  )
}
